
import React from 'react';
import { Box } from '@mui/material';
import { colors, spacing, radius } from 'common/theme';
import { TypographyVariant } from 'common/theme/tokens/typography/scale';
import { FontWeight } from 'common/theme/tokens/typography/fontWeight';
import { COUPON_STATUS } from '../../utils/enums';
import { useTranslations } from 'common/hooks/useTranslations';

export const StatusBadge = ({ status, testID, isMobile = false }) => {
  const { translate } = useTranslations();

  const STATUS_CONFIG = {
    [COUPON_STATUS.PENDING]: {
      label: translate('coupon_status_pending'),
      bgColor: colors.system.badge.warning?.bg || '#FFF3E0',
      textColor: colors.system.badge.warning?.text || '#E65100',
    },
    [COUPON_STATUS.SUCCESS]: {
      label: translate('coupon_status_success'),
      bgColor: colors.system.badge.success.bg,
      textColor: colors.system.badge.success.text,
    },
    [COUPON_STATUS.REFUND]: {
      label: translate('coupon_status_refund'),
      bgColor: colors.system.badge.expired.bg,
      textColor: colors.system.badge.expired.text,
    },
    [COUPON_STATUS.PARTIAL_REFUND]: {
      label: translate('coupon_status_partial_refund'),
      bgColor: colors.system.badge.expired.bg,
      textColor: colors.system.badge.expired.text,
    },
    [COUPON_STATUS.IN_PROGRESS_REQUEST]: {
      label: translate('coupon_status_in_progress_request'),
      bgColor: colors.system.badge.warning?.bg || '#FFF3E0',
      textColor: colors.system.badge.warning?.text || '#E65100',
    },
    [COUPON_STATUS.NOT_CANCELLABLE]: {
      label: translate('coupon_status_not_cancellable'),
      bgColor: colors.system.badge.expired.bg,
      textColor: colors.system.badge.expired.text,
    },
    [COUPON_STATUS.EXPIRED]: {
      label: translate('coupon_status_expired'),
      bgColor: colors.palette.gray100,
      textColor: colors.palette.blackAlpha500,
    },
    [COUPON_STATUS.PARTIALLY_USED]: {
      label: translate('coupon_status_partially_used'),
      bgColor: colors.system.badge.warning?.bg || '#FFF3E0',
      textColor: colors.system.badge.warning?.text || '#E65100',
    },
    [COUPON_STATUS.FULLY_USED]: {
      label: translate('coupon_status_fully_used'),
      bgColor: colors.palette.gray100,
      textColor: colors.palette.blackAlpha500,
    },
  };

  const config = STATUS_CONFIG[status] || STATUS_CONFIG[COUPON_STATUS.SUCCESS];

  return (
    <Box
      data-testid={testID}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        textAlign: 'center',
        justifyContent: 'center',
        borderRadius: `${radius.md}px`,
        backgroundColor: config.bgColor,
        maxWidth: { xs: 'auto', lg: 60 },
        color: config.textColor,
        fontSize: 14,
        fontWeight: FontWeight.bold,
        padding: isMobile ? `${spacing.xs}px ${spacing.sm}px` : `${spacing.xs}px ${spacing.xl}px`,
      }}
    >
      {config.label}
    </Box>
  );
};

const MULTIPASS_BUDGET_BADGE_CONFIG = {
  [COUPON_STATUS.SUCCESS]: {
    bgColor: colors.system.badge.success.bg,
    textColor: colors.system.badge.success.text,
  },
  [COUPON_STATUS.PARTIALLY_USED]: {
    bgColor: colors.system.badge.warning?.bg || '#FFF3E0',
    textColor: colors.system.badge.warning?.text || '#E65100',
  },
  [COUPON_STATUS.FULLY_USED]: {
    bgColor: colors.palette.gray100,
    textColor: colors.palette.blackAlpha500,
  },
};


export const BudgetBalanceBadge = ({ status, remainingAmount, testID, isMobile = false }) => {
  const { translate } = useTranslations();
  const config =
    MULTIPASS_BUDGET_BADGE_CONFIG[status] || MULTIPASS_BUDGET_BADGE_CONFIG[COUPON_STATUS.SUCCESS];


  return (
    <Box
      data-testid={testID}
      dir="rtl"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: `${spacing.xs}px`,
        maxWidth: '100%',
        minWidth: 0,
        lineHeight: 1.2,
      }}
    >
      <Box
        component="span"
        sx={{
          fontSize: 14,
          fontWeight: FontWeight.medium,
          color: colors.system.text.subtle,
          minWidth: 0,
          maxWidth: '100%',
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
          textAlign: 'right',
        }}
      >
        {translate('my_coupons_multipass_balance_label')}
      </Box>
      <Box
        component="span"
        dir="ltr"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          whiteSpace: 'nowrap',
          borderRadius: `${radius.md}px`,
          backgroundColor: config.bgColor,
          color: config.textColor,
          fontSize: 14,
          fontWeight: FontWeight.bold,
          padding: isMobile ? `${spacing.xs}px ${spacing.sm}px` : `${spacing.xs}px ${spacing.md}px`,
          unicodeBidi: 'isolate',
        }}
      >
        ₪{remainingAmount}
      </Box>
    </Box>
  );
};

export default StatusBadge;

